import { useEffect, useRef } from "react";

export function SmokeOverlay({
  intensity = 1.0, // 0.5 a 2.0
  maxFps = 40, // 30-45 recomendado
  dprCap = 1.5, // limita custo em telas retina
  opacity = 0.9, // transparência do overlay
  zIndex = 9999,
  mixBlendMode = "screen",
  interactive = true,
  colorStrength = 1,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl =
      canvas.getContext("webgl", {
        alpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: true,
      }) || canvas.getContext("experimental-webgl");

    if (!gl) {
      // fallback: não faz nada
      return;
    }

    // ======= Shaders =======
    const vs = `
      attribute vec2 a_position;
      varying vec2 v_uv;
      void main() {
        v_uv = (a_position + 1.0) * 0.5;
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    // Fumaça "fake" leve: noise + fbm + rastro do mouse (sem ping-pong)
    // (Pode parecer magia, mas é barato: 1 draw call).
    const fs = `
      precision mediump float;
      varying vec2 v_uv;

      uniform vec2 u_res;
      uniform float u_time;
      uniform vec2 u_mouse;    // 0..1
      uniform vec2 u_mvel;     // mouse velocity (px/frame) normalizada
      uniform float u_intensity;
      uniform float u_opacity;
      uniform float u_color_strength;

      // Hash / noise leves
      float hash(vec2 p){
        p = fract(p*vec2(123.34, 456.21));
        p += dot(p, p+45.32);
        return fract(p.x*p.y);
      }

      float noise(vec2 p){
        vec2 i = floor(p);
        vec2 f = fract(p);
        float a = hash(i);
        float b = hash(i+vec2(1.0,0.0));
        float c = hash(i+vec2(0.0,1.0));
        float d = hash(i+vec2(1.0,1.0));
        vec2 u = f*f*(3.0-2.0*f);
        return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
      }

      float fbm(vec2 p){
        float v = 0.0;
        float a = 0.5;
        // 4 octaves (barato)
        for(int i=0;i<4;i++){
          v += a * noise(p);
          p *= 2.0;
          a *= 0.5;
        }
        return v;
      }

      vec3 palette(float t){
        // paleta suave colorida
        vec3 a = vec3(0.55, 0.50, 0.65);
        vec3 b = vec3(0.45, 0.40, 0.35);
        vec3 c = vec3(1.0, 1.0, 1.0);
        vec3 d = vec3(0.00, 0.15, 0.35);
        return a + b*cos(6.28318*(c*t + d));
      }

      void main(){
        vec2 uv = v_uv;
        // Corrige aspect para distorções ficarem bonitas
        vec2 p = (uv - 0.5);
        p.x *= u_res.x / u_res.y;

        float t = u_time * 0.12;

        // "flow" base
        vec2 flow = vec2(
          fbm(p*1.2 + vec2(t, -t)),
          fbm(p*1.2 + vec2(-t, t))
        );

        // distorção leve
        uv += (flow - 0.5) * 0.03;

        // rastro do ponteiro: uma nuvem que aumenta com velocidade
        vec2 m = u_mouse;
        vec2 d = (v_uv - m);
        d.x *= u_res.x / u_res.y;
        float speed = clamp(length(u_mvel) * 6.0, 0.0, 1.5);

        float blob = exp(-dot(d,d) * (12.0 - speed*6.0)); // foco do mouse
        float swirl = fbm(d*5.0 + u_time*0.6);
        float smoke = fbm((uv-0.5)*2.0 + flow + u_time*0.15);

        // mistura: base + influência do mouse
        float alpha = (smoke*0.65 + blob*(0.55 + swirl*0.65)*u_intensity) * u_opacity;

        // suaviza e controla
        alpha = smoothstep(0.15, 0.85, alpha) * 0.9;
        alpha *= 0.75;

        // cor varia no tempo e no campo
        float c = smoke + blob*0.8 + sin(u_time*0.2)*0.05;
        vec3 col = palette(c);

        // deixa mais "fumacinha"
        col = mix(col, vec3(1.0), 0.08);
        col = mix(vec3(0.88, 0.9, 0.92), col, u_color_strength);

        gl_FragColor = vec4(col, alpha);
      }
    `;

    function compileShader(type, source) {
      const s = gl.createShader(type);
      if (!s) return null;

      gl.shaderSource(s, source);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(s));
        gl.deleteShader(s);
        return null;
      }
      return s;
    }

    const vsh = compileShader(gl.VERTEX_SHADER, vs);
    const fsh = compileShader(gl.FRAGMENT_SHADER, fs);
    if (!vsh || !fsh) return;

    const program = gl.createProgram();
    if (!program) return;

    gl.attachShader(program, vsh);
    gl.attachShader(program, fsh);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      return;
    }
    gl.useProgram(program);

    // Fullscreen quad
    const buf = gl.createBuffer();
    if (!buf) return;

    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    const aPos = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    // Uniforms
    const uRes = gl.getUniformLocation(program, "u_res");
    const uTime = gl.getUniformLocation(program, "u_time");
    const uMouse = gl.getUniformLocation(program, "u_mouse");
    const uMvel = gl.getUniformLocation(program, "u_mvel");
    const uIntensity = gl.getUniformLocation(program, "u_intensity");
    const uOpacity = gl.getUniformLocation(program, "u_opacity");
    const uColorStrength = gl.getUniformLocation(program, "u_color_strength");

    // Blend (overlay transparente)
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Resize + DPR cap
    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, dprCap);
      const w = Math.floor(window.innerWidth * dpr);
      const h = Math.floor(window.innerHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        gl.viewport(0, 0, w, h);
        gl.uniform2f(uRes, w, h);
      }
    }

    resize();
    window.addEventListener("resize", resize);

    // Mouse tracking (normalizado 0..1)
    let mouse = { x: 0.5, y: 0.5 };
    let prev = { x: 0.5, y: 0.5 };
    let vel = { x: 0.0, y: 0.0 };

    function updatePointer(clientX, clientY) {
      const x = clientX / window.innerWidth;
      const y = 1.0 - clientY / window.innerHeight;
      // velocity (suavizada)
      vel.x = x - prev.x;
      vel.y = y - prev.y;
      prev.x = x;
      prev.y = y;

      // suaviza posição
      mouse.x = mouse.x * 0.85 + x * 0.15;
      mouse.y = mouse.y * 0.85 + y * 0.15;
    }

    function onMove(e) {
      updatePointer(e.clientX, e.clientY);
    }

    function onTouchMove(e) {
      const touch = e.touches[0];
      if (!touch) return;

      updatePointer(touch.clientX, touch.clientY);
    }

    if (interactive) {
      window.addEventListener("mousemove", onMove, { passive: true });
      window.addEventListener("touchstart", onTouchMove, { passive: true });
      window.addEventListener("touchmove", onTouchMove, { passive: true });
    }

    // FPS cap
    let last = 0;
    const minDt = 1000 / Math.max(10, maxFps);

    // Pause when tab hidden
    let running = true;
    function onVis() {
      running = !document.hidden;
    }
    document.addEventListener("visibilitychange", onVis);

    const start = performance.now();

    function frame(now) {
      rafRef.current = requestAnimationFrame(frame);
      if (!running) return;

      const dt = now - last;
      if (dt < minDt) return;
      last = now;

      resize();

      const time = (now - start) / 1000;

      // Decai a velocidade (rastro some)
      vel.x *= 0.92;
      vel.y *= 0.92;

      if (!interactive) {
        mouse.x = 0.5 + Math.sin(time * 0.18) * 0.28;
        mouse.y = 0.48 + Math.cos(time * 0.14) * 0.2;
        vel.x = 0.0;
        vel.y = 0.0;
      }

      gl.uniform1f(uTime, time);
      gl.uniform2f(uMouse, mouse.x, mouse.y);
      gl.uniform2f(uMvel, vel.x, vel.y);
      gl.uniform1f(uIntensity, intensity);
      gl.uniform1f(uOpacity, opacity);
      gl.uniform1f(uColorStrength, colorStrength);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      if (interactive) {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("touchstart", onTouchMove);
        window.removeEventListener("touchmove", onTouchMove);
      }
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intensity, maxFps, dprCap, opacity, interactive, colorStrength]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex,
        mixBlendMode,
      }}
    />
  );
}
